/*
 * Screen-level layout shared by the back-office screens (DESIGN.md §6.3, §7, §10).
 * Tokens only. Components themselves live in packages/ui; this file only arranges them.
 */

/* A page's column of parts, filling the width the shell gives it (no max-width of its own). */
.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
}

/* A wide table scrolls inside its own box, so the page never scrolls sideways (§10). */
.tableScroll {
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
}

/* A table of six or more columns below this width would crush its text into one word a
 * line; it keeps its width and the box scrolls instead. Smaller tables just fit. */
.tableScroll table:has(th:nth-child(6)) {
  min-width: 40rem;
}

/* Queue left, work right (§6.3): 320px queue, the work takes the rest. */
.split {
  display: grid;
  grid-template-columns: 20rem minmax(0, 1fr);
  gap: var(--space-5);
  align-items: start;
}

/* Budget laptops at 1024px: a narrower queue leaves the work area room for its table. */
@media (width < 1280px) {
  .split {
    grid-template-columns: 16rem minmax(0, 1fr);
  }
}

/* Phones and narrow tablets: the queue sits above the work. */
@media (width < 768px) {
  .split {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Links between the pages of one area, drawn as a segmented control (ADR 0019, ADR 0028):
 * a soft blue-grey track, the current page a white pill with a primary label. On a phone
 * they stay on one line and scroll sideways. */
.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  width: fit-content;
  max-width: 100%;
  padding: 3px;
  border-radius: var(--radius-control);
  background: var(--wash);
}

.tabs a {
  display: inline-flex;
  align-items: center;
  min-height: calc(var(--control-height) - 8px);
  padding: 0 var(--space-3);
  border-radius: calc(var(--radius-control) - 2px);
  color: var(--pencil);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color var(--dur) var(--ease),
    color var(--dur) var(--ease);
}

.tabs a:hover {
  color: var(--ink);
}

.tabs a[aria-current='page'] {
  background: var(--paper);
  box-shadow: var(--shadow-pill);
  color: var(--brand);
  font-weight: 500;
}

.tabs a:focus-visible {
  outline: var(--focus-ring);
  outline-offset: -2px;
}

@media (width < 768px) {
  .tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
  }
}

@media (pointer: coarse) {
  .tabs a {
    min-height: calc(var(--touch-target) - 6px);
  }
}

/* A filter bar: fields side by side, the button lined up with the inputs, not the helper text. */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--space-3) var(--space-4);
}

.filters > * {
  flex: 0 1 14rem;
  min-width: 0;
}

/* The button sits on the input line: a label's height (22px) plus the field's gap (4px). */
.filtersAction {
  flex: 0 0 auto;
  padding-top: calc(22px + var(--space-1));
}

/* Label and amount pairs with the amounts right-aligned in one column (shift, totals). */
.amounts {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  max-width: 28rem;
  margin: 0;
}

.amounts dt,
.amounts dd {
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--rule);
}

.amounts dt {
  padding-right: var(--space-5);
}

.amounts dt {
  color: var(--graphite);
}

.amounts dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.amounts dt[data-total],
.amounts dd[data-total] {
  border-bottom: 0;
  color: var(--ink);
  font: var(--t-body-strong);
  font-variant-numeric: tabular-nums;
}

/* "Press P to take payment." Small, secondary, next to the action it names. */
.hint {
  color: var(--graphite);
  font: var(--t-small);
}

/* Touch screens have no keyboard, and the sentence reads wrong without its key cap. */
@media (hover: none), (pointer: coarse) {
  .hint {
    display: none;
  }
}

/* An untitled card, for a page whose content has no natural section heading (a settings list,
 * a stock form, the cashier queue). The card surface comes from the same tokens as packages/ui
 * Section (ADR 0019), phone padding included, so the two can't drift apart. */
.panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
  padding: var(--card-padding);
  border: var(--card-border);
  border-radius: var(--radius-card);
  background: var(--paper);
  box-shadow: var(--shadow-card);
}
